import { css } from "@emotion/react"
import styled from "@emotion/styled"
import {
	colorCanvas,
	shadowSm,
	durationSm,
	easeInOut,
	spacingFluidSm,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren, ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { ContentWrapper } from "../contentWrapper/ContentWrapper"

const DURATION = Number.parseFloat(durationSm) * 1000

type VisibilityState = "visible" | "invisible" | "fadeIn" | "fadeOut"

type StickyHeaderProps = PropsWithChildren<{
	forTableHead?: boolean
	rightChild?: ReactNode
}>

export const StickyHeader = ({ children, forTableHead, rightChild }: StickyHeaderProps) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const [state, setState] = useState<VisibilityState>("invisible")
	const [timer, setTimer] = useState<NodeJS.Timeout>()

	const handleScroll = () => {
		if (!containerRef.current) {
			return
		}

		// using el.getBoundingClientRect() for top relative to the viewport + scroll position
		// while el.offsetTop is only relative to parent element
		const offsetTop = containerRef.current.getBoundingClientRect().top + window.scrollY

		if ((state === "visible" || state === "fadeIn") && window.scrollY < offsetTop) {
			clearTimeout(timer)
			setState("fadeOut")
			const timerInvisible = setTimeout(() => setState("invisible"), DURATION)
			setTimer(timerInvisible)
		} else if ((state === "invisible" || state === "fadeOut") && window.scrollY > offsetTop) {
			clearTimeout(timer)
			setState("fadeIn")
			const timerVisible = setTimeout(() => setState("visible"), DURATION)
			setTimer(timerVisible)
		}
	}

	useEffect(() => {
		window.addEventListener("scroll", handleScroll)

		return () => {
			window.removeEventListener("scroll", handleScroll)
		}
	})

	return (
		<Container ref={containerRef}>
			<InnerWrapper $state={state}>
				<StyledContentWrapper $forTableHead={forTableHead}>
					{children}
					{rightChild && <RightChildWrapper>{rightChild}</RightChildWrapper>}
				</StyledContentWrapper>
			</InnerWrapper>
		</Container>
	)
}

const Container = styled.div`
	position: relative;
	height: 100%;
`

const InnerWrapper = styled("div", { shouldForwardProp })<{ $state: VisibilityState }>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 95;
	box-shadow: ${shadowSm};
	background: ${colorCanvas};
	transform: ${({ $state }) =>
		$state === "visible" || $state === "fadeIn" ? "translateY(0%)" : "translateY(-100%)"};
	transition: transform ${durationSm} ${easeInOut};
	visibility: ${({ $state }) => ($state === "invisible" ? "hidden" : "visible")};
`

const StyledContentWrapper = styled(ContentWrapper)<{ $forTableHead?: boolean }>`
	display: flex;
	align-items: center;
	gap: ${spacingFluidSm};
	${({ $forTableHead }) =>
		$forTableHead
			? css`
					margin-bottom: -5px; /* get rid of 1px border-bottom + 4px scroller padding */
					> * {
						flex-basis: 100%; /* stretch table to 100% width */
					}
				`
			: css`
		min-height: 70px; /* to match other sticky headers containing button (54px + 2 * spacingStaticSm) */
		padding-top: ${spacingStaticSm};
		padding-bottom: ${spacingStaticSm};
	`}
`

const RightChildWrapper = styled.div`
	margin-inline-start: auto;
`
